/**
 * Zaměření firmy: proklikávaný seznam a vlastní popis.
 *
 * Dřívější verze odkrývala úrovně pod sebou, takže po kliknutí na Výrobu
 * stránka narostla a odskočila. Tady se obsah seznamu VYMĚNÍ: vybraná
 * úroveň zmizí a nastoupí její podobory. Výška panelu se drží, návrat
 * vede drobečkovou cestou nad seznamem.
 *
 * Vedle seznamu je popis vlastními slovy. Číselník nikdy nepokryje
 * všechno a klikání nutí klienta hádat, kam jeho firma patří. Kdo napíše,
 * čím se živí, dostane zařazení odvozené a může ho přijmout nebo přepsat.
 * Popis má přednost: zná svou firmu líp než číselník.
 */
import React, { useEffect, useRef, useState } from 'react';
import { nabidkaProUroven, nactiObory, nazvyCesty } from '../api/oboryApi';
import { rozpoznejObor } from '../api/oboryApi';

// Kolik položek se vejde, než seznam dostane vlastní posuvník. Bez toho
// by patnáct oborů e-shopu odsunulo tlačítko Pokračovat pod okraj.
const MAX_VYSKA_SEZNAMU = '19rem';

export function VyberOboru({ typ, cesta = [], vlastni = '', onChange, chyba }) {
  const [strom, setStrom] = useState(null);
  const [chybaNacteni, setChybaNacteni] = useState(null);
  const [popis, setPopis] = useState(vlastni);
  const [rozpoznava, setRozpoznava] = useState(false);
  const [navrh, setNavrh] = useState(null);
  const [chybaAi, setChybaAi] = useState(null);
  const posledniPopis = useRef(vlastni);

  useEffect(() => {
    let platne = true;
    nactiObory()
      .then(s => { if (platne) setStrom(s); })
      .catch(e => { if (platne) setChybaNacteni(e.message); });
    return () => { platne = false; };
  }, []);

  if (chybaNacteni) return <div className="error-message">{chybaNacteni}</div>;
  if (!strom) return <Blok><Napoveda>Načítáme číselník oborů</Napoveda></Blok>;

  const nabidka = nabidkaProUroven(strom, typ, cesta, cesta.length);
  const nazvy = nazvyCesty(strom, typ, cesta);
  const maCiselnik = ((strom[typ] || []).length) > 0;

  const otevri = (klic) => onChange([...cesta, klic], popis);
  const zpetNa = (uroven) => onChange(cesta.slice(0, uroven), popis);

  const zmenPopis = (text) => {
    setPopis(text);
    setNavrh(null);
    setChybaAi(null);
    onChange(cesta, text);
  };

  const rozpoznej = async () => {
    setRozpoznava(true); setChybaAi(null); setNavrh(null);
    try {
      const v = await rozpoznejObor(popis);
      posledniPopis.current = popis;
      if (v.rozpoznano) setNavrh(v);
      else setChybaAi('Z popisu se obor určit nepodařilo. Vyberte ho ze seznamu, '
        + 'nebo popis doplňte o to, co firma vyrábí či prodává.');
    } catch (e) {
      setChybaAi(e.message);
    } finally {
      setRozpoznava(false);
    }
  };

  const prijmiNavrh = () => {
    onChange(navrh.cesta, popis, navrh.typ);
    setNavrh(null);
  };

  return (
    <Blok chyba={chyba}>
      <label style={{ display: 'block', marginBottom: '0.3rem' }}>
        Čemu se firma věnuje?
      </label>
      <Napoveda>
        Podle zaměření hledáme srovnatelné firmy ve sbírce listin. Čím
        přesněji ho určíme, tím bližší konkurenty najdeme.
      </Napoveda>

      {maCiselnik && (
        <>
          <Drobecky nazvy={nazvy} zpetNa={zpetNa} />
          {nabidka.length > 0 ? (
            <div style={{
              maxHeight: MAX_VYSKA_SEZNAMU, overflowY: 'auto',
              border: '1px solid var(--purple-mid)',
              borderRadius: 'var(--radius, 4px)', marginTop: '0.5rem',
            }}>
              {nabidka.map((o, i) => (
                <Radek key={o.klic} obor={o} prvni={i === 0} onClick={() => otevri(o.klic)} />
              ))}
            </div>
          ) : (
            <Hotovo nazev={nazvy[nazvy.length - 1]} onZmenit={() => zpetNa(cesta.length - 1)} />
          )}
        </>
      )}

      <div style={{
        marginTop: '1.1rem', paddingTop: '0.9rem',
        borderTop: '1px solid var(--purple-mid)',
      }}>
        <label htmlFor="obor-popis" style={{ display: 'block', fontSize: '0.84rem' }}>
          {maCiselnik ? 'Nebo firmu popište vlastními slovy' : 'Popište, čím se firma živí'}
        </label>
        <Napoveda>
          Napište, co vyrábíte nebo prodáváte a komu. Zařazení z toho
          odvodíme a vy ho potvrdíte.
        </Napoveda>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.45rem', flexWrap: 'wrap' }}>
          <input
            id="obor-popis"
            type="text"
            value={popis}
            onChange={e => zmenPopis(e.target.value)}
            placeholder="např. vyrábíme barefoot boty a prodáváme je přes vlastní e-shop"
            style={{ flex: '1 1 18rem', minWidth: 0 }}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={rozpoznej}
            disabled={rozpoznava || popis.trim().length < 3}
          >
            {rozpoznava ? 'Určuji obor' : 'Určit obor z popisu'}
          </button>
        </div>
        {navrh && <Navrh navrh={navrh} onPrijmout={prijmiNavrh} onZamitnout={() => setNavrh(null)} />}
        {chybaAi && (
          <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-dim)' }}>
            {chybaAi}
          </small>
        )}
      </div>
    </Blok>
  );
}

function Drobecky({ nazvy, zpetNa }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.3rem',
      marginTop: '0.7rem', fontSize: '0.84rem',
    }}>
      <Krok popis="Vše" aktivni={nazvy.length === 0} onClick={() => zpetNa(0)} />
      {nazvy.map((n, i) => (
        <React.Fragment key={n}>
          <span aria-hidden="true" style={{ color: 'var(--text-dim)' }}>›</span>
          <Krok popis={n} aktivni={i === nazvy.length - 1} onClick={() => zpetNa(i + 1)} />
        </React.Fragment>
      ))}
    </div>
  );
}

function Krok({ popis, aktivni, onClick }) {
  if (aktivni) {
    return (
      <span style={{
        fontFamily: "'Source Serif 4', Georgia, serif",
        color: 'var(--gold)', fontSize: '0.95rem',
      }}>{popis}</span>
    );
  }
  return (
    <button type="button" onClick={onClick} style={{
      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
      font: 'inherit', color: 'var(--text-dim)', textDecoration: 'underline',
    }}>{popis}</button>
  );
}

function Radek({ obor, prvni, onClick }) {
  const maDalsi = (obor.deti || []).length > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', textAlign: 'left', cursor: 'pointer',
        padding: '0.6rem 0.8rem', background: 'transparent',
        border: 'none', borderTop: prvni ? 'none' : '1px solid var(--purple-mid)',
        color: 'var(--star-white)', font: 'inherit', fontSize: '0.88rem',
      }}
    >
      <span>{obor.nazev}</span>
      {maDalsi && <span aria-hidden="true" style={{ color: 'var(--text-dim)' }}>›</span>}
    </button>
  );
}

function Hotovo({ nazev, onZmenit }) {
  return (
    <div style={{
      marginTop: '0.6rem', padding: '0.7rem 0.85rem',
      border: '1px solid var(--gold)', borderRadius: 'var(--radius, 4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '0.8rem', flexWrap: 'wrap',
    }}>
      <span>
        <span style={{
          display: 'block', fontSize: '0.66rem', letterSpacing: '0.16em',
          textTransform: 'uppercase', color: 'var(--text-dim)', fontWeight: 600,
        }}>Vybráno</span>
        <span style={{
          fontFamily: "'Source Serif 4', Georgia, serif",
          fontSize: '1.02rem', color: 'var(--gold)',
        }}>{nazev}</span>
      </span>
      <button type="button" onClick={onZmenit} style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        font: 'inherit', fontSize: '0.83rem', color: 'var(--text-dim)',
        textDecoration: 'underline',
      }}>Změnit</button>
    </div>
  );
}

function Navrh({ navrh, onPrijmout, onZamitnout }) {
  return (
    <div style={{
      marginTop: '0.7rem', padding: '0.75rem 0.85rem',
      border: '1px solid var(--gold)', borderRadius: 'var(--radius, 4px)',
    }}>
      <div style={{
        fontSize: '0.66rem', letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--text-dim)', fontWeight: 600, marginBottom: '0.2rem',
      }}>Z popisu vychází</div>
      <div style={{
        fontFamily: "'Source Serif 4', Georgia, serif",
        fontSize: '1.05rem', color: 'var(--gold)',
      }}>{navrh.nazev}</div>
      {navrh.duvod && (
        <div style={{ fontSize: '0.83rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>
          {navrh.duvod}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn-primary" onClick={onPrijmout}>
          Použít toto zařazení
        </button>
        <button type="button" className="btn-secondary" onClick={onZamitnout}>
          Vyberu ze seznamu
        </button>
      </div>
    </div>
  );
}

function Blok({ children, chyba }) {
  return (
    <div style={{
      background: 'rgba(107,63,160,0.06)',
      border: '1px solid rgba(107,63,160,0.3)',
      borderRadius: 12, padding: '1rem 1.1rem', marginBottom: '1.5rem',
    }}>
      {children}
      {chyba && <small className="chyba-pole">{chyba}</small>}
    </div>
  );
}

function Napoveda({ children }) {
  return (
    <small style={{ color: 'var(--text-dim)', display: 'block', fontSize: '0.8rem' }}>
      {children}
    </small>
  );
}

export default VyberOboru;
